import { datadogLogs } from '@datadog/browser-logs';
import { Config } from '../config';
import { getDeviceModel } from '../device';
import { Logger } from '../logger';

const LOG_TAG = 'DATADOG';

let initialized = false;

/**
 * Initializes the Datadog browser-logs SDK.
 *
 * Uses only `@datadog/browser-logs` (not RUM) — the RUM SDK depends on
 * `PerformanceObserver` and `MutationObserver` which are unavailable in
 * the Cast receiver environment.
 *
 * Should be called once during app startup, after the Cast context is ready
 * (device detection needs `CastReceiverContext`).
 */
export function initDatadog(): void {
  const clientToken = Config.DD_CLIENT_TOKEN;
  if (!clientToken) {
    Logger.warn(LOG_TAG, 'DD_CLIENT_TOKEN not set — Datadog disabled');
    return;
  }

  datadogLogs.init({
    clientToken,
    site: Config.DD_SITE || 'datadoghq.com',
    service: 'chromecast-receiver',
    env: Config.NODE_ENV === 'production' ? 'prod' : 'dev',
    version: Config.BUILD_VERSION,
    forwardErrorsToLogs: true,
    forwardConsoleLogs: [],
    sessionSampleRate: 10,
  });

  datadogLogs.setGlobalContextProperty('deviceModel', getDeviceModel());

  initialized = true;
  Logger.info(LOG_TAG, 'Datadog initialized');
}

/**
 * Sets (or updates) the user context on Datadog.
 * Called after the user-channel receives the token and fetches the profile.
 */
export function setDatadogUser(userId: string, userName: string): void {
  if (!initialized) return;

  datadogLogs.setUser({ id: userId, name: userName });
  Logger.debug(LOG_TAG, 'User context set', { userId });
}

/**
 * Logs an error to Datadog with structured context.
 */
export function datadogError(
  tag: string,
  error: unknown,
  context?: Record<string, unknown>,
): void {
  if (!initialized) return;

  const message = error instanceof Error ? error.message : String(error);
  datadogLogs.logger.error(`[${tag}] ${message}`, context, error instanceof Error ? error : undefined);
}

/**
 * Logs a warning to Datadog with structured context.
 */
export function datadogWarn(
  tag: string,
  message: string,
  context?: Record<string, unknown>,
): void {
  if (!initialized) return;

  datadogLogs.logger.warn(`[${tag}] ${message}`, context);
}
